import BackButton from "@/features/shared/components/back-button";
import { useLng } from "@/hooks/use-lng";
import { formatAppTitle } from "@/lib/app-document-title";
import { getCookie } from "@/lib/cookies";
import { markdownQueryOptions } from "@/lib/markdown";
import { getShortLngFromCulture } from "@/utils/get-short-lng";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import styles from "./cookies-page.module.css";

function getCookiesTitle(lng: string): string {
  if (lng === "sv") return "Cookie och integritetspolicy";
  if (lng === "en") return "Cookie and Privacy Policy";
  if (lng === "da") return "Cookie og integritetspolitik";
  if (lng === "de") return "Cookie und Integritätspolitik";
  return "";
}

function getCookiesDescription(lng: string): string {
  if (lng === "sv")
    return "Vi använder cookies för att webbplatsen ska fungera, komma ihåg dina val och ge dig den bästa upplevelsen.";
  if (lng === "en")
    return "We use cookies to make the website work, remember your choices and give you the best experience.";
  if (lng === "da")
    return "Vi använder cookies för at webbstedet skal fungere, huske dine val og give dig den bedste oplevelse.";
  if (lng === "de")
    return "Wir verwenden Cookies, um die Website zu funktionieren, Ihre Auswahl zu erinnern und Ihnen die beste Erfahrung zu geben.";
  return "";
}

export const Route = createFileRoute("/_app/cookies/")({
  head: () => {
    const lng = getShortLngFromCulture(getCookie("shop-culture"));
    return {
      meta: [
        { title: formatAppTitle(getCookiesTitle(lng)) },
        { name: "description", content: getCookiesDescription(lng) }
      ]
    };
  },
  component: CookiesPage
});

function CookiesPage() {
  const lng = useLng();
  const { t } = useTranslation();
  const { data: markdown, isLoading } = useQuery(markdownQueryOptions("cookies", lng));

  return (
    <div className={`${styles.page} mx-auto space-y-10 px-4 py-4 md:py-8`}>
      <BackButton />
      <div className="prose prose-a:text-primary prose-h1:text-foreground prose-h2:text-foreground prose-h3:text-foreground prose-h4:text-foreground prose-h5:text-foreground prose-h6:text-foreground prose-th:text-foreground prose-td:text-foreground prose-code:text-foreground prose-code:before:content-none prose-code:after:content-none prose-h2:mb-1 pt-4">
        <h1>{t("cookies-policy-title")}</h1>

        <h2>{t("cookies-what-are-cookies")}</h2>

        <p>{t("cookies-what-are-cookies-description")}</p>

        <h2>{t("cookies-manage-cookies-in-your-browser")}</h2>

        <p>{t("cookies-manage-cookies-in-your-browser-description")}</p>

        <h2>{t("cookies-our-cookies")}</h2>

        <p>{t("cookies-our-cookies-description")}</p>

        <table>
          <thead>
            <tr>
              <th>{t("cookies-names")}</th>
              <th>{t("cookies-purpose")}</th>
              <th>{t("cookies-storage-time")}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <code>shop-culture</code>
              </td>
              <td>{t("cookies-save-your-culture")}</td>
              <td>{t("cookies-session-or-until-you-change-language")}</td>
            </tr>
            <tr>
              <td>
                <code>i18next</code>
              </td>
              <td>
                {t("cookies-language-setting-used-by-translation-system")}
                <code>shop-culture</code>.
              </td>
              <td>{t("cookies-session-or-until-you-change-language")}</td>
            </tr>
            <tr>
              <td>
                <code>shop-currency</code>
              </td>
              <td>{t("cookies-save-currency")}</td>
              <td>{t("cookies-save-currency-description")}</td>
            </tr>
            <tr>
              <td>
                <code>shop-saleId</code>
              </td>
              <td>{t("cookies-save-saleid")}</td>
              <td>{t("cookies-save-saleid-description")}</td>
            </tr>
            <tr>
              <td>
                <code>membersapp-consent</code>
              </td>
              <td>{t("cookies-save-membersapp-consent")}</td>
              <td>{t("cookies-one-year")}</td>
            </tr>
            <tr>
              <td>
                <code>ee-members-app-theme</code>
              </td>
              <td>{t("cookies-save-membersapp-theme")}</td>
              <td>{t("cookies-one-year")}</td>
            </tr>
          </tbody>
        </table>

        {isLoading ? (
          <>Loading...</>
        ) : markdown ? (
          <ReactMarkdown rehypePlugins={[rehypeRaw]}>{markdown}</ReactMarkdown>
        ) : null}
      </div>
    </div>
  );
}
